import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { ChannelSearchDialogComponent } from '@components/channel-search-dialog/channel-search-dialog.component';
import { Channel } from 'icreate/common/models/channel';
import { ChannelType, getDigitalTypesSet } from 'icreate/common/models/channel-type';
import { RunningHourGroup } from 'icreate/common/models/running-hour-group';
import { TranslateService } from '@ngx-translate/core';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { lastValueFrom, take } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';

@Component({
    selector: 'app-running-hour-row',
    templateUrl: './running-hour-row.component.html',
    styleUrls: ['./running-hour-row.component.css'],
    standalone: false,
})
export class RunningHourRowComponent {
    @Input() runningHourGroup: RunningHourGroup;
    @Input() index: number;
    @Input() vesselId: string;
    @Input() runningHourChannel: Channel;
    @Input() dataSourceChannel: Channel | null = null;
    @Input() configuredRunningHourGroups: RunningHourGroup[] = [];
    @Input() canEdit: boolean = false;

    @Input() isDropdownOpen = false;
    @Output() toggleDropdown = new EventEmitter<string>();
    @Output() closed = new EventEmitter<void>();

    @Output() groupUpdated = new EventEmitter<boolean>();
    @Output() dataSourceChannelChanged = new EventEmitter<{ groupId: string; channel: Channel }>();

    @ViewChild('removeDataSourceConfirmation')
    removeDataSourceConfirmation: ConfirmationWindowComponent;
    @ViewChild('resetStatusConfirmation') resetStatusConfirmation: ConfirmationWindowComponent;

    @ViewChild('dropdownWrapper') dropdownWrapper!: ElementRef;
    @ViewChild('dropdownButton', { static: false, read: ElementRef }) dropdownButton!: ElementRef;
    @ViewChild('dropdownContainer') dropdownContainer!: ElementRef;
    @ViewChild('infoIcon', { static: false, read: ElementRef }) infoIcon!: ElementRef;
    @ViewChild('infoMessageContainer') infoMessageContainer!: ElementRef;

    isLoading: boolean = true;
    isInfoMessageVisible = false;

    infoTop = 0;
    infoLeft = 0;

    private scrollContainer: HTMLElement | null = null;
    private tableContent: HTMLElement | null = null;

    allowedTypesSet: Set<ChannelType> = getDigitalTypesSet();

    selectedDigitalStatus: string | null = null;
    selectedMotorStatuses: Map<string, number> = new Map();
    dataSourceStatusesList: String[];

    isConfigurationsDone: boolean = false;

    constructor(
        private translate: TranslateService,
        private dialog: MatDialog,
        private elementRef: ElementRef
    ) {}

    @HostListener('window:resize')
    onWindowChange() {
        if (this.isDropdownOpen) {
            this.closed.emit();
        }
    }

    ngAfterViewInit() {
        this.scrollContainer = this.elementRef.nativeElement.closest('.table-content');
        this.tableContent = this.elementRef.nativeElement.closest('.table-data');
    }

    ngOnDestroy() {
        this.releaseDropdownScrollSpace();
    }

    @HostListener('document:click', ['$event'])
    onClickOutside(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        const clickedInside = this.dropdownWrapper?.nativeElement.contains(target);

        if (!clickedInside && this.isDropdownOpen) {
            this.closed.emit();
        }
    }

    async ngOnInit() {
        this.isLoading = true;
        this.initSelectedDigitalStatus();
        this.initSelectedMotorStatus();
        this.initStatusesLists();
        this.isLoading = false;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['isDropdownOpen']) {
            if (changes['isDropdownOpen'].currentValue) {
                Promise.resolve().then(() => {
                    this.reserveDropdownScrollSpace();
                });
            } else {
                this.releaseDropdownScrollSpace();
            }
        }

        if (!changes['dataSourceChannel'] || changes['dataSourceChannel'].firstChange) return;

        this.selectedDigitalStatus = null;
        this.selectedMotorStatuses = new Map();
        this.dataSourceStatusesList = [];
        this.isConfigurationsDone = false;
        this.initSelectedDigitalStatus();
        this.initSelectedMotorStatus();
        this.initStatusesLists();
    }

    initSelectedDigitalStatus() {
        if (!this.dataSourceChannel?.fcuData?.digitalAlarmSetting) return;
        const digitalSettings = this.dataSourceChannel.fcuData.digitalAlarmSetting;
        if (this.runningHourGroup?.statusOn) {
            this.selectedDigitalStatus = digitalSettings.statusStringContactOn;
            this.isConfigurationsDone = true;
        } else if (this.runningHourGroup?.statusOff) {
            this.selectedDigitalStatus = digitalSettings.statusStringContactOff;
            this.isConfigurationsDone = true;
        } else {
            this.selectedDigitalStatus = null;
        }
    }

    initSelectedMotorStatus() {
        if (!this.dataSourceChannel?.fcuData?.motorChannelSetting) return;
        const motorChannelSetting = this.dataSourceChannel.fcuData.motorChannelSetting;

        if (this.runningHourGroup?.motorChValue) {
            const motorStatusBinary = this.getDecimalToBinary(this.runningHourGroup.motorChValue)
                .split('')
                .reverse()
                .join('');
            for (let i = 1; i <= 4; i++) {
                if (motorStatusBinary[i] === '1') {
                    const statusString = motorChannelSetting[`runStatusString${i}`];
                    if (statusString) {
                        this.selectedMotorStatuses.set(statusString, i);
                        this.isConfigurationsDone = true;
                    }
                }
            }
        }
    }

    initStatusesLists() {
        if (this.dataSourceChannel) {
            if (this.dataSourceChannel?.fcuData?.channelType === ChannelType.DIGITAL_CONTACT) {
                if (!this.dataSourceChannel?.fcuData?.digitalAlarmSetting) return;
                const digitalAlarmSettings = this.dataSourceChannel.fcuData.digitalAlarmSetting;
                this.dataSourceStatusesList = [
                    digitalAlarmSettings.statusStringContactOn,
                    digitalAlarmSettings.statusStringContactOff,
                ];
            } else if (this.dataSourceChannel?.fcuData?.channelType === ChannelType.DIGITAL_MOTOR) {
                if (!this.dataSourceChannel?.fcuData?.motorChannelSetting) return;
                const motorChannelSetting = this.dataSourceChannel.fcuData.motorChannelSetting;
                this.dataSourceStatusesList = [
                    motorChannelSetting.runStatusString1,
                    motorChannelSetting.runStatusString2,
                    motorChannelSetting.runStatusString3,
                    motorChannelSetting.runStatusString4,
                ].filter(status => status !== '');
            }
        }
    }

    getBinaryToDecimal(binary: string) {
        return parseInt(binary, 2);
    }

    getDecimalToBinary(decimal: number) {
        return decimal.toString(2);
    }

    getDataSourceName() {
        return Number(this.dataSourceChannel.opsData.tag) + '\u00A0\u00A0' + this.dataSourceChannel.opsData.name;
    }

    resetStatuses() {
        this.runningHourGroup.statusOn = false;
        this.runningHourGroup.statusOff = false;
        this.runningHourGroup.motorChValue = null;
        this.selectedDigitalStatus = null;
        this.selectedMotorStatuses = new Map();
        this.dataSourceStatusesList = [];
        this.isConfigurationsDone = false;
        this.initStatusesLists();
        this.triggerChange();
    }

    triggerChange() {
        this.groupUpdated.emit(true);
    }

    reset() {
        if (!this.canEdit) return;
        this.groupUpdated.emit(false);
    }

    isDigitalChannel() {
        return this.dataSourceChannel?.fcuData.channelType === ChannelType.DIGITAL_CONTACT;
    }

    isMotorChannel() {
        return this.dataSourceChannel?.fcuData.channelType === ChannelType.DIGITAL_MOTOR;
    }

    onSelectSingleStatus(status: string): void {
        if (!this.canEdit) return;
        if (status === this.dataSourceChannel?.fcuData?.digitalAlarmSetting?.statusStringContactOn) {
            this.runningHourGroup.statusOn = true;
            this.runningHourGroup.statusOff = false;
            this.selectedDigitalStatus = this.dataSourceChannel?.fcuData?.digitalAlarmSetting?.statusStringContactOn;
            this.triggerChange();
            this.closeDropdown();
        } else if (status === this.dataSourceChannel?.fcuData?.digitalAlarmSetting?.statusStringContactOff) {
            this.runningHourGroup.statusOn = false;
            this.runningHourGroup.statusOff = true;
            this.selectedDigitalStatus = this.dataSourceChannel?.fcuData?.digitalAlarmSetting?.statusStringContactOff;
            this.triggerChange();
            this.closeDropdown();
        }
    }

    toggleStatusByRowClick(status: string, index: number, event: MouseEvent): void {
        if (!this.canEdit) return;
        const clickedElement = event.target as HTMLElement;
        if (clickedElement instanceof HTMLElement && clickedElement.closest('mat-checkbox')) {
            return;
        }
        const isSelected = this.selectedMotorStatuses.has(status);
        this.onToggleMotorStatus(status, index, !isSelected);
    }

    onToggleMotorStatus(status: string, index: number, checked: boolean): void {
        const bitmask = 1 << (index + 1);
        let changed = false;

        if (checked) {
            if (!this.selectedMotorStatuses.has(status)) {
                this.selectedMotorStatuses.set(status, index + 1);
                this.runningHourGroup.motorChValue += bitmask;
                changed = true;
            }
        } else {
            const storedIndex = this.selectedMotorStatuses.get(status);
            if (storedIndex !== undefined) {
                this.selectedMotorStatuses.delete(status);
                this.runningHourGroup.motorChValue -= 1 << storedIndex;
                changed = true;
            }
        }

        if (this.runningHourGroup.motorChValue === 0) this.runningHourGroup.motorChValue = null;
        if (changed) this.triggerChange();
    }

    get selectedStatusLabel(): string {
        if (this.isDigitalChannel()) {
            return (
                this.selectedDigitalStatus ||
                this.translate.instant('runnning-hour-group.dialog.select-input.digital.status')
            );
        }
        if (this.isMotorChannel()) {
            const keys = Array.from(this.selectedMotorStatuses.keys());
            return keys.length > 0
                ? keys.map(key => `(${key})`).join(' ')
                : this.translate.instant('runnning-hour-group.dialog.select-input.digital.status');
        }
        return this.translate.instant('runnning-hour-group.dialog.select-input.digital.status');
    }

    async openSelectChannelDialog() {
        if (!this.canEdit) return;
        try {
            const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
                data: {
                    vesselId: this.vesselId,
                    assignedChannel: this.dataSourceChannel,
                    allowedTypesSet: this.allowedTypesSet,
                    setShouldDisableTypes: true,
                },
            });

            const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());
            if (channelSelected) {
                this.dataSourceChannel = channelSelected;
                this.runningHourGroup.dataSouceChannelId = this.dataSourceChannel.id;
                this.dataSourceChannelChanged.emit({
                    groupId: this.runningHourGroup.id,
                    channel: this.dataSourceChannel,
                });
                this.resetStatuses();
            }
        } catch (error) {
            console.log(error);
        }
    }

    removeChannel() {
        if (!this.canEdit) return;
        this.dataSourceChannel = null;
        this.runningHourGroup.dataSouceChannelId = null;
        this.dataSourceChannelChanged.emit({
            groupId: this.runningHourGroup.id,
            channel: this.dataSourceChannel,
        });
        this.isConfigurationsDone = false;
        this.triggerChange();
    }

    async resetStatusDialog() {
        if (!this.canEdit) return;
        this.resetStatusConfirmation.setVisible(true);
        let confirmationOption = await lastValueFrom(this.resetStatusConfirmation.onOptionSelected.pipe(take(1)));
        if (confirmationOption === ConfirmationWindowTypes.SAVE) {
            this.resetStatuses();
        }
    }

    async removeChannelDialog() {
        if (!this.canEdit) return;
        this.removeDataSourceConfirmation.setVisible(true);
        let confirmationOption = await lastValueFrom(this.removeDataSourceConfirmation.onOptionSelected.pipe(take(1)));
        if (confirmationOption === ConfirmationWindowTypes.SAVE) {
            this.removeChannel();
        }
    }

    get isStatusSet(): boolean {
        if (this.isDigitalChannel()) {
            return !!this.selectedDigitalStatus;
        }
        if (this.isMotorChannel()) {
            return this.selectedMotorStatuses && this.selectedMotorStatuses.size > 0;
        }
        return false;
    }

    onToggleClick(): void {
        if (!this.canEdit) return;
        this.toggleDropdown.emit(this.runningHourGroup.id);
    }

    private reserveDropdownScrollSpace() {
        if (!this.scrollContainer || !this.tableContent || !this.dropdownContainer) return;

        const visibleBottom = Math.min(this.scrollContainer.getBoundingClientRect().bottom, window.innerHeight);
        const overflow = this.dropdownContainer.nativeElement.getBoundingClientRect().bottom - visibleBottom;

        if (overflow > 0) this.tableContent.style.paddingBottom = `${overflow}px`;
    }

    private releaseDropdownScrollSpace() {
        if (this.tableContent) this.tableContent.style.removeProperty('padding-bottom');
    }

    closeDropdown(): void {
        this.closed.emit();
    }

    openInfoMessage() {
        this.isInfoMessageVisible = true;

        setTimeout(() => {
            this.updatePosition();
        });
    }

    closeInfoMessage() {
        this.isInfoMessageVisible = false;
    }

    private updatePosition() {
        if (!this.infoIcon || !this.infoMessageContainer) return;

        const iconRect = this.infoIcon.nativeElement.getBoundingClientRect();
        const boxRect = this.infoMessageContainer.nativeElement.getBoundingClientRect();

        const vw = window.innerWidth;
        const vh = window.innerHeight;

        const tooltipOffset = 2;
        const screenEdgeBuffer = 10;

        let left = iconRect.right + tooltipOffset;
        let top = iconRect.top;

        if (left + boxRect.width > vw) {
            left = iconRect.left - boxRect.width - tooltipOffset;
        }

        if (top + boxRect.height > vh) {
            top = vh - boxRect.height - screenEdgeBuffer;
        }

        if (top < 0) {
            top = screenEdgeBuffer;
        }

        this.infoTop = top;
        this.infoLeft = left;
    }
}
